/**
 * Campaigns service
 *
 * Firestore layout:
 *   Campaigns (collection)
 *     PopUp (doc)
 *       campaigns (subcollection)
 *         {campaignId} (doc)
 *
 * Storage layout:
 *   campaigns/popup/{campaignId}/{timestamp}_{filename}
 */
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { getFirebaseDb } from '@core/firebase/firebase.js'
import { uploadFileWithProgress, deleteFile } from '@core/firebase/storage.js'

const POPUP_PARENT_DOC = ['Campaigns', 'PopUp']
const POPUP_SUBCOLLECTION = 'campaigns'

function popUpCollection() {
  return collection(getFirebaseDb(), ...POPUP_PARENT_DOC, POPUP_SUBCOLLECTION)
}

function popUpDoc(id) {
  return doc(getFirebaseDb(), ...POPUP_PARENT_DOC, POPUP_SUBCOLLECTION, id)
}

export function subscribePopUpCampaigns(callback) {
  const q = query(popUpCollection(), orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
    },
    (err) => {
      console.error('subscribePopUpCampaigns failed:', err)
      callback([])
    }
  )
}

export async function getPopUpCampaign(id) {
  const snap = await getDoc(popUpDoc(id))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

/**
 * Create or update a pop-up campaign.
 * @param {Object} params
 * @param {string|null} params.id existing campaign id (null = create)
 * @param {Object} params.data { title, description, startDate (Date), endDate (Date) }
 * @param {File|null} params.mediaFile new media file or null to keep existing
 * @param {(progress:number)=>void} [params.onProgress] upload progress 0-100
 * @returns {Promise<string>} campaign id
 */
export async function savePopUpCampaign({ id, data, mediaFile, onProgress }) {
  const isNew = !id
  const ref = isNew ? doc(popUpCollection()) : popUpDoc(id)
  const campaignId = ref.id

  if (isNew && !mediaFile) {
    throw new Error('יש להעלות קובץ מדיה (תמונה או וידאו) לקמפיין חדש')
  }

  let mediaUpdate = null
  if (mediaFile) {
    if (!isNew) {
      const existing = await getDoc(ref)
      const oldPath = existing.exists() ? existing.data()?.mediaPath : null
      if (oldPath) {
        await deleteFile(oldPath).catch((err) =>
          console.warn('Failed to delete previous media:', err)
        )
      }
    }
    const safeName = sanitizeFileName(mediaFile.name)
    const storagePath = `campaigns/popup/${campaignId}/${Date.now()}_${safeName}`
    const downloadUrl = await uploadFileWithProgress(storagePath, mediaFile, onProgress)
    mediaUpdate = {
      mediaType: mediaFile.type.startsWith('video/') ? 'video' : 'image',
      mediaUrl: downloadUrl,
      mediaPath: storagePath,
      mediaContentType: mediaFile.type || '',
    }
  }

  const payload = {
    title: data.title,
    description: data.description,
    startDate: toTimestamp(data.startDate),
    endDate: toTimestamp(data.endDate),
    updatedAt: serverTimestamp(),
  }
  if (mediaUpdate) Object.assign(payload, mediaUpdate)

  if (isNew) {
    await setDoc(ref, { ...payload, createdAt: serverTimestamp() })
  } else {
    await updateDoc(ref, payload)
  }
  return campaignId
}

export async function deletePopUpCampaign(campaign) {
  if (!campaign?.id) return
  if (campaign.mediaPath) {
    await deleteFile(campaign.mediaPath).catch((err) =>
      console.warn('Failed to delete media file:', err)
    )
  }
  await deleteDoc(popUpDoc(campaign.id))
}

/**
 * Regenerate a pop-up campaign's UID by creating a fresh doc with a new id and
 * deleting the old one. Optionally applies field/media edits in the same flow.
 * The new id is what users key their "already seen" state on, so this surfaces
 * the campaign again to viewers who already dismissed the previous version.
 *
 * Media handling: if `mediaFile` is provided, the old media is replaced (old
 * file deleted, new file uploaded under the new campaign id). Otherwise the
 * new doc reuses the existing `mediaUrl`/`mediaPath` and the old storage file
 * is preserved (we only delete the old Firestore doc).
 *
 * @param {Object} params
 * @param {string} params.id existing campaign id (required)
 * @param {Object} [params.data] optional field updates { title, description, startDate, endDate }
 * @param {File|null} [params.mediaFile] optional new media to replace existing
 * @param {(progress:number)=>void} [params.onProgress] upload progress 0-100
 * @returns {Promise<string>} new campaign id
 */
export async function refreshPopUpCampaign({ id, data = null, mediaFile = null, onProgress }) {
  if (!id) throw new Error('Missing campaign id')

  const oldSnap = await getDoc(popUpDoc(id))
  if (!oldSnap.exists()) throw new Error('הקמפיין לא נמצא')
  const oldData = oldSnap.data()

  const newRef = doc(popUpCollection())
  const newId = newRef.id

  let mediaUpdate = null
  if (mediaFile) {
    const safeName = sanitizeFileName(mediaFile.name)
    const storagePath = `campaigns/popup/${newId}/${Date.now()}_${safeName}`
    const downloadUrl = await uploadFileWithProgress(storagePath, mediaFile, onProgress)
    mediaUpdate = {
      mediaType: mediaFile.type.startsWith('video/') ? 'video' : 'image',
      mediaUrl: downloadUrl,
      mediaPath: storagePath,
      mediaContentType: mediaFile.type || '',
    }
    if (oldData.mediaPath) {
      await deleteFile(oldData.mediaPath).catch((err) =>
        console.warn('Failed to delete previous media:', err)
      )
    }
  }

  const payload = {
    title: data?.title ?? oldData.title,
    description: data?.description ?? oldData.description,
    startDate: data?.startDate ? toTimestamp(data.startDate) : oldData.startDate || null,
    endDate: data?.endDate ? toTimestamp(data.endDate) : oldData.endDate || null,
    mediaType: mediaUpdate?.mediaType ?? oldData.mediaType ?? '',
    mediaUrl: mediaUpdate?.mediaUrl ?? oldData.mediaUrl ?? '',
    mediaPath: mediaUpdate?.mediaPath ?? oldData.mediaPath ?? '',
    mediaContentType: mediaUpdate?.mediaContentType ?? oldData.mediaContentType ?? '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  await setDoc(newRef, payload)
  // Delete the old Firestore doc only — media at oldData.mediaPath stays so
  // the new doc keeps a valid reference (unless we just replaced it above).
  await deleteDoc(popUpDoc(id))
  return newId
}

export function isCampaignActive(campaign, now = new Date()) {
  const start = toJsDate(campaign?.startDate)
  const end = toJsDate(campaign?.endDate)
  if (!start || !end) return false
  const endOfDay = new Date(end)
  endOfDay.setHours(23, 59, 59, 999)
  return now >= start && now <= endOfDay
}

function toJsDate(v) {
  if (!v) return null
  if (v instanceof Date) return v
  if (typeof v.toDate === 'function') return v.toDate()
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000)
  if (typeof v === 'string' || typeof v === 'number') return new Date(v)
  return null
}

function toTimestamp(v) {
  if (!v) return null
  if (v instanceof Date) return Timestamp.fromDate(v)
  if (typeof v?.toDate === 'function') return v
  if (typeof v === 'string') return Timestamp.fromDate(new Date(v))
  return null
}

function sanitizeFileName(name) {
  return String(name).replace(/[^\w.\-]+/g, '_')
}

/* -------------------------------------------------------------------------- *
 * B2B Divur Campaigns (Campains collection)                                  *
 * Independent from the PopUp campaigns above. Used by the b2b-divur "Main"   *
 * tab to manage WhatsApp marketing campaigns.                                *
 * -------------------------------------------------------------------------- */

const DIVUR_COLLECTION = 'Campains'

function divurCollection() {
  return collection(getFirebaseDb(), DIVUR_COLLECTION)
}

function divurDoc(id) {
  return doc(getFirebaseDb(), DIVUR_COLLECTION, id)
}

/**
 * Reserve a new divur campaign doc reference so callers can upload images to a
 * deterministic Storage path (`campaigns/{id}/...`) before the Firestore doc
 * is written.
 * @returns {{ id: string, ref: import('firebase/firestore').DocumentReference }}
 */
export function newDivurCampaignRef() {
  const ref = doc(divurCollection())
  return { id: ref.id, ref }
}

/**
 * Upload a single image for a divur campaign to Storage.
 * @param {string} campaignId
 * @param {File} file
 * @param {(progress:number)=>void} [onProgress]
 * @returns {Promise<{ url: string, path: string }>}
 */
export async function uploadDivurCampaignImage(campaignId, file, onProgress) {
  const safeName = sanitizeFileName(file.name || 'image')
  const path = `campaigns/${campaignId}/${Date.now()}_${safeName}`
  const url = await uploadFileWithProgress(path, file, onProgress)
  return { url, path }
}

/**
 * Create a new divur campaign at the pre-reserved doc ref.
 * @param {import('firebase/firestore').DocumentReference} ref
 * @param {Object} payload - { title, description, images, imagePaths, contacts }
 * @param {Object} actor - { uid, displayName }
 * @returns {Promise<{ id: string }>}
 */
export async function createDivurCampaign(ref, payload, actor) {
  const data = {
    title: String(payload.title || '').trim(),
    description: String(payload.description || '').trim(),
    images: Array.isArray(payload.images) ? payload.images : [],
    imagePaths: Array.isArray(payload.imagePaths) ? payload.imagePaths : [],
    contacts: Array.isArray(payload.contacts) ? payload.contacts : [],
    sent: false,
    createdAt: serverTimestamp(),
    createdBy: {
      uid: actor?.uid || null,
      displayName: actor?.displayName || '',
    },
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: actor?.uid || null,
      displayName: actor?.displayName || '',
    },
  }
  await setDoc(ref, data)
  return { id: ref.id }
}

/**
 * Update an existing divur campaign. Preserves `sent`, `createdAt`, `createdBy`.
 * @param {string} campaignId
 * @param {Object} payload - { title, description, images, imagePaths, contacts }
 * @param {Object} actor - { uid, displayName }
 */
export async function updateDivurCampaign(campaignId, payload, actor) {
  const data = {
    title: String(payload.title || '').trim(),
    description: String(payload.description || '').trim(),
    images: Array.isArray(payload.images) ? payload.images : [],
    imagePaths: Array.isArray(payload.imagePaths) ? payload.imagePaths : [],
    contacts: Array.isArray(payload.contacts) ? payload.contacts : [],
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: actor?.uid || null,
      displayName: actor?.displayName || '',
    },
  }
  await updateDoc(divurDoc(campaignId), data)
}

/**
 * Mark a divur campaign as sent. Sets `sent: true`, `sentAt`, `sentBy` and bumps
 * `updatedAt`/`updatedBy`. Other fields are untouched.
 * @param {string} campaignId
 * @param {Object} actor - { uid, displayName }
 */
export async function markDivurCampaignSent(campaignId, actor) {
  const data = {
    sent: true,
    sentAt: serverTimestamp(),
    sentBy: {
      uid: actor?.uid || null,
      displayName: actor?.displayName || '',
    },
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: actor?.uid || null,
      displayName: actor?.displayName || '',
    },
  }
  await updateDoc(divurDoc(campaignId), data)
}

/**
 * Delete specific Storage files (best-effort).
 * @param {string[]} paths
 */
export async function deleteDivurCampaignImages(paths) {
  if (!Array.isArray(paths) || paths.length === 0) return
  await Promise.all(
    paths.map((path) =>
      deleteFile(path).catch((err) => console.warn('Failed to delete campaign image:', path, err))
    )
  )
}

/**
 * Subscribe to all divur campaigns (newest first).
 * @param {(campaigns: Object[]) => void} callback
 * @returns {Function} Unsubscribe function
 */
export function subscribeDivurCampaigns(callback) {
  const q = query(divurCollection(), orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
    },
    (err) => {
      console.error('subscribeDivurCampaigns failed:', err)
      callback([])
    }
  )
}

/**
 * Delete a divur campaign and its uploaded images.
 * @param {Object} campaign - { id, imagePaths? }
 */
export async function deleteDivurCampaign(campaign) {
  if (!campaign?.id) return
  if (Array.isArray(campaign.imagePaths)) {
    await Promise.all(
      campaign.imagePaths.map((path) =>
        deleteFile(path).catch((err) => console.warn('Failed to delete campaign image:', path, err))
      )
    )
  }
  await deleteDoc(divurDoc(campaign.id))
}
